import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Dropdown from "@/components/ui/dropdown";
import DatePicker from "@/components/ui/datepicker";
import {
  Close as CloseIcon,
  AddEmployee as AddEmployeeIcon,
} from "@/components/icons";
import type {
  ApplicationSource,
  InternTypeV2,
  InternSubCategory,
  InternLocation,
  ApplicationSourceV2,
} from "../types/intern-management.types";
import {
  InternStatus,
  splitInternTypeLegacy,
} from "../types/intern-management.types";
import {
  internManagementApi,
  INTERNSHIP_TYPE_V2_TO_API,
  SUB_CATEGORY_TO_API,
  INTERN_LOCATION_TO_API,
} from "../services/intern-management.api";
import { translateInternApiError } from "../utils/error-mapper";
import { fetchDepartments } from "@/features/announcements/services/departments.api";
import { getAdminUsers } from "@/lib/users.api";
import styles from "./AddInternModal.module.scss";

/** TR IBAN: "TR" + 24 rakam = 26 karakter. Boşlukları temizleyip kontrol eder. */
const TR_IBAN_REGEX = /^TR\d{24}$/i;
const normalizeIban = (val: string) => val.replace(/\s+/g, "").toUpperCase();

const SUPERVISOR_PAGE_SIZE = 20;
const SUPERVISOR_DEBOUNCE_MS = 300;

/** Sadece rakamlardan oluşan 10 haneli telefon (gönderirken başına +90 eklenir) */
const phoneDigitsOnly = (val: string) => val.replace(/\D/g, "");
const PHONE_PLACEHOLDER = "5369834415";

// ─── Step 1 Validasyonu (Kişisel Bilgiler) ───────────────────────────
// Spec (17.04.2026): TC, Ad Soyad, Telefon zorunlu; E-posta, IBAN, Adres opsiyonel
const addInternStep1Schema = z
  .object({
    tc: z
      .string()
      .min(1, "T.C. kimlik no zorunludur")
      .refine((v) => /^\d{11}$/.test(v.replace(/\s/g, "")), {
        message: "internManagement.addModal.validation.tcLength",
      }),
    name: z.string().min(1, "Ad soyad zorunludur"),
    phone: z
      .string()
      .min(1, "internManagement.addModal.validation.phoneRequired")
      .refine((val) => phoneDigitsOnly(val).length === 10, {
        message: "internManagement.addModal.validation.phoneLength",
      }),
    email: z
      .string()
      .optional()
      .refine(
        (v) => !v || v.trim() === "" || z.string().email().safeParse(v).success,
        { message: "internManagement.addModal.validation.emailInvalid" },
      ),
    iban: z
      .string()
      .optional()
      .refine(
        (v) => !v || v.trim() === "" || TR_IBAN_REGEX.test(normalizeIban(v)),
        { message: "internManagement.addModal.validation.ibanInvalid" },
      ),
  })
  .superRefine((val, ctx) => {
    const nameParts = val.name.trim().split(/\s+/).filter(Boolean);
    if (nameParts.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["name"],
        message: "Ad ve soyad zorunludur",
      });
    }
  });

// ─── Dropdown seçenekleri (t() ile çağıran component içinden üretilir) ───
const buildInternTypeOptions = (t: (k: string) => string): { label: string; value: InternTypeV2 }[] => [
  { label: t("internManagement.addModal.internTypeOptions.university"), value: "UNIVERSITY" },
  { label: t("internManagement.highSchool"), value: "HIGH_SCHOOL" },
];

const buildUniversitySubOptions = (t: (k: string) => string): { label: string; value: InternSubCategory }[] => [
  { label: t("internManagement.addModal.internTypeOptions.mandatorySummer"), value: "MANDATORY_SUMMER" },
  { label: t("internManagement.addModal.internTypeOptions.workplaceTraining"), value: "WORKPLACE_TRAINING" },
];

const buildHighSchoolSubOptions = (t: (k: string) => string): { label: string; value: InternSubCategory }[] => [
  { label: t("internManagement.addModal.internTypeOptions.mandatorySummer"), value: "MANDATORY_SUMMER" },
  { label: t("internManagement.addModal.internTypeOptions.skillTraining"), value: "SKILL_TRAINING" },
];

const buildLocationOptions = (t: (k: string) => string): { label: string; value: InternLocation }[] => [
  { label: t("internManagement.addModal.departmentTypeOptions.central"), value: "CENTER" },
  { label: t("internManagement.addModal.departmentTypeOptions.provincial"), value: "PROVINCIAL" },
];

const buildApplicationSourceV2Options = (t: (k: string) => string): {
  label: string;
  value: ApplicationSourceV2;
}[] => [
  { label: t("internManagement.addModal.applicationSourceOptions.careerGate"), value: "CAREER_GATE" },
  { label: t("internManagement.addModal.applicationSourceOptions.inPerson"), value: "IN_PERSON" },
];

const buildStatusOptions = (t: (k: string) => string) => [
  { label: t("internManagement.addModal.statusOptions.active"), value: "0" },
  { label: t("internManagement.addModal.statusOptions.completed"), value: "1" },
];

// ─── Form State ─────────────────────────────────────────────────────
export interface AddInternFormData {
  // Step 1 — Kişisel Bilgiler
  tc: string;
  name: string;
  phone: string;
  email: string;
  iban: string;
  address: string;

  // Step 2 — Staj Bilgileri
  internType: InternTypeV2 | null;
  subCategory: InternSubCategory | null;
  location: InternLocation | null;
  departmentID: string | null;
  startDate: Date | null;
  endDate: Date | null;
  status: InternStatus;
  supervisorID: string | null;
  supervisorName: string;
  supervisorContact: string;
  applicationSource: ApplicationSourceV2 | null;

  // Step 2 — Lise özel alanları
  parentName: string;
  parentContact: string;
  coordinatorName: string;
  coordinatorContact: string;

  // Step 3 — Akademik Bilgiler
  school: string;
  department: string;
  class: string;
  schoolNumber: string;
}

const getInitialFormData = (): AddInternFormData => ({
  tc: "",
  name: "",
  phone: "",
  email: "",
  iban: "",
  address: "",
  internType: null,
  subCategory: null,
  location: null,
  departmentID: null,
  startDate: null,
  endDate: null,
  status: InternStatus.AKTIF,
  supervisorID: null,
  supervisorName: "",
  supervisorContact: "",
  applicationSource: null,
  parentName: "",
  parentContact: "",
  coordinatorName: "",
  coordinatorContact: "",
  school: "",
  department: "",
  class: "",
  schoolNumber: "",
});

interface AddInternModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data: AddInternFormData) => void;
  /** Düzenleme modu: verilen id için stajyer yüklenir ve PATCH ile güncellenir */
  editId?: string | null;
}

const AddInternModal: React.FC<AddInternModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editId,
}) => {
  const { t } = useTranslation();
  const INTERN_TYPE_OPTIONS = useMemo(() => buildInternTypeOptions(t), [t]);
  const UNIVERSITY_SUB_OPTIONS = useMemo(() => buildUniversitySubOptions(t), [t]);
  const HIGH_SCHOOL_SUB_OPTIONS = useMemo(() => buildHighSchoolSubOptions(t), [t]);
  const LOCATION_OPTIONS = useMemo(() => buildLocationOptions(t), [t]);
  const APPLICATION_SOURCE_V2_OPTIONS = useMemo(() => buildApplicationSourceV2Options(t), [t]);
  const STATUS_OPTIONS = useMemo(() => buildStatusOptions(t), [t]);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] =
    useState<AddInternFormData>(getInitialFormData);
  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [supervisorOptions, setSupervisorOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isLoadingSupervisor, setIsLoadingSupervisor] = useState(false);
  const supervisorDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Koşullu: Lise ise Şahsen Başvuru otomatik seçili. Ünv+İşYeriEğ ise de.
  const isHighSchool = formData.internType === "HIGH_SCHOOL";
  const isWorkplaceTraining = formData.subCategory === "WORKPLACE_TRAINING";
  const lockApplicationSourceToInPerson = isHighSchool || isWorkplaceTraining;

  // Alt kategori seçenekleri internType'a göre değişir
  const subCategoryOptions = useMemo(() => {
    if (formData.internType === "UNIVERSITY") return UNIVERSITY_SUB_OPTIONS;
    if (formData.internType === "HIGH_SCHOOL") return HIGH_SCHOOL_SUB_OPTIONS;
    return [];
  }, [formData.internType]);

  // internType değişince subCategory'yi temizle (seçilen alt kategori artık geçersizse)
  useEffect(() => {
    if (!formData.internType) return;
    const validValues = subCategoryOptions.map((o) => o.value);
    if (
      formData.subCategory &&
      !validValues.includes(formData.subCategory)
    ) {
      setFormData((prev) => ({ ...prev, subCategory: null }));
    }
  }, [formData.internType, formData.subCategory, subCategoryOptions]);

  // Koşullu applicationSource otomatiği
  useEffect(() => {
    if (lockApplicationSourceToInPerson) {
      setFormData((prev) =>
        prev.applicationSource === "IN_PERSON"
          ? prev
          : { ...prev, applicationSource: "IN_PERSON" },
      );
    }
  }, [lockApplicationSourceToInPerson]);

  useEffect(() => {
    if (isOpen && editId) {
      setEditLoading(true);
      internManagementApi
        .getById(editId)
        .then((res) => {
          const d = res.data;
          const rawPhone = d.phone ?? "";
          const displayPhone = rawPhone.replace(/^\+90/, "").trim() || rawPhone;
          // Yeni spec alanları geliyorsa onları kullan, yoksa eski combined'ten türet
          const split = d.internTypeV2
            ? { internTypeV2: d.internTypeV2, subCategory: d.subCategory }
            : splitInternTypeLegacy(d.internType);
          setFormData({
            tc: d.tc ?? "",
            name: d.name ?? "",
            phone: displayPhone,
            email: d.email ?? "",
            iban: d.iban ?? "",
            address: d.address ?? "",
            internType: (split.internTypeV2 as InternTypeV2) ?? null,
            subCategory: (split.subCategory as InternSubCategory) ?? null,
            location: d.location ?? null,
            departmentID: d.departmentID ?? null,
            startDate: d.startDate ? new Date(d.startDate) : null,
            endDate: d.endDate ? new Date(d.endDate) : null,
            status: (d.status ?? InternStatus.AKTIF) as InternStatus,
            supervisorID: d.supervisorID ?? null,
            supervisorName: d.supervisorName ?? "",
            supervisorContact: d.supervisorContact ?? "",
            applicationSource: mapLegacyApplicationSourceToV2(
              d.applicationSource,
            ),
            parentName: d.parentName ?? "",
            parentContact: d.parentContact ?? "",
            coordinatorName: d.coordinatorName ?? "",
            coordinatorContact: d.coordinatorContact ?? "",
            school: d.school ?? "",
            department: d.schoolDepartment ?? "",
            class: d.grade ?? "",
            schoolNumber: d.schoolNumber ?? "",
          });
          const supervisorId = d.supervisorID ?? null;
          const supervisorName = d.supervisorName ?? "";
          if (supervisorId && supervisorName) {
            setSupervisorOptions((prev) =>
              prev.some((o) => o.value === supervisorId)
                ? prev
                : [{ label: supervisorName, value: supervisorId }, ...prev],
            );
          }
        })
        .catch(() => setFormData(getInitialFormData()))
        .finally(() => setEditLoading(false));
    } else if (isOpen) {
      setFormData(getInitialFormData());
    }
    if (isOpen) setFieldErrors({});
  }, [isOpen, editId]);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const typeFilter = formData.location
      ? (INTERN_LOCATION_TO_API[formData.location] as 1 | 2)
      : undefined;
    fetchDepartments({ page: 1, limit: 100, type: typeFilter })
      .then((list) =>
        setDepartmentOptions(
          list.map((d) => ({ label: d.name, value: d.id })),
        ),
      )
      .catch(() => setDepartmentOptions([]));
  }, [isOpen, formData.location]);

  const fetchSupervisors = useCallback(
    async (search: string, currentId?: string, currentLabel?: string) => {
      setIsLoadingSupervisor(true);
      try {
        const { data } = await getAdminUsers({
          limit: SUPERVISOR_PAGE_SIZE,
          page: 1,
          search: search.trim() || undefined,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
        const options = data.map((u) => {
          const label =
            [u.firstName, u.lastName].filter(Boolean).join(" ") ||
            u.email ||
            u.id;
          return { label, value: u.id };
        });
        if (
          currentId &&
          currentLabel &&
          !options.some((o) => o.value === currentId)
        ) {
          setSupervisorOptions([
            { label: currentLabel, value: currentId },
            ...options,
          ]);
        } else {
          setSupervisorOptions(options);
        }
      } catch {
        setSupervisorOptions(
          currentId && currentLabel
            ? [{ label: currentLabel, value: currentId }]
            : [],
        );
      } finally {
        setIsLoadingSupervisor(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isOpen) return;
    fetchSupervisors("", undefined, undefined);
  }, [isOpen, fetchSupervisors]);

  const handleSupervisorSearch = (value: string) => {
    if (supervisorDebounceRef.current)
      clearTimeout(supervisorDebounceRef.current);
    supervisorDebounceRef.current = setTimeout(() => {
      fetchSupervisors(
        value,
        formData.supervisorID ?? undefined,
        formData.supervisorName || undefined,
      );
      supervisorDebounceRef.current = null;
    }, SUPERVISOR_DEBOUNCE_MS);
  };

  const handleSupervisorSelect = (value: string | string[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    if (!v) {
      setFormData((prev) => ({
        ...prev,
        supervisorID: null,
        supervisorName: "",
      }));
      return;
    }
    const opt = supervisorOptions.find((o) => o.value === v);
    setFormData((prev) => ({
      ...prev,
      supervisorID: v,
      supervisorName: opt?.label ?? prev.supervisorName,
    }));
  };

  // ─── Validasyonlar ─────────────────────────────────────────────
  const validateStep1 = (): boolean => {
    const result = addInternStep1Schema.safeParse({
      tc: formData.tc,
      name: formData.name.trim(),
      phone: formData.phone,
      email: formData.email?.trim() ?? "",
      iban: formData.iban?.trim() ?? "",
    });
    if (result.success) {
      setFieldErrors({});
      return true;
    }
    const flattened = result.error.flatten().fieldErrors;
    const errors: Record<string, string> = {};
    if (flattened.tc?.[0]) errors.tc = flattened.tc[0];
    if (flattened.name?.[0]) errors.name = flattened.name[0];
    if (flattened.phone?.[0]) errors.phone = flattened.phone[0];
    if (flattened.email?.[0]) errors.email = flattened.email[0];
    if (flattened.iban?.[0]) errors.iban = flattened.iban[0];
    setFieldErrors(errors);
    return false;
  };

  const validateStep2 = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.internType) errors.internType = t("internManagement.addModal.validation.internTypeRequired");
    if (!formData.subCategory)
      errors.subCategory = "Staj alt kategorisi zorunludur";
    if (!formData.location) errors.location = "Staj yeri zorunludur";
    if (!formData.departmentID)
      errors.departmentID = t("internManagement.addModal.validation.departmentRequired");
    if (!formData.startDate) errors.startDate = t("internManagement.addModal.validation.startDateRequired");
    if (!formData.endDate) errors.endDate = t("internManagement.addModal.validation.endDateRequired");
    // Lise için veli & koordinatör ad + iletişim zorunlu (spec 17.04.2026)
    if (isHighSchool) {
      if (!formData.parentName.trim())
        errors.parentName = t("internManagement.addModal.validation.parentNameRequired");
      if (!formData.parentContact.trim())
        errors.parentContact = t("internManagement.validation.parentContactRequired");
      if (!formData.coordinatorName.trim())
        errors.coordinatorName = t("internManagement.addModal.validation.coordinatorNameRequired");
      if (!formData.coordinatorContact.trim())
        errors.coordinatorContact = t("internManagement.validation.coordinatorContactRequired");
    }
    setFieldErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.school.trim()) errors.school = "Okul zorunludur";
    if (!formData.department.trim()) errors.department = t("internManagement.addModal.validation.schoolDepartmentRequired");
    if (!formData.class.trim()) errors.class = t("internManagement.addModal.validation.classRequired");
    setFieldErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  const isStep1Valid = () =>
    addInternStep1Schema.safeParse({
      tc: formData.tc,
      name: formData.name.trim(),
      phone: formData.phone,
      email: formData.email?.trim() ?? "",
      iban: formData.iban?.trim() ?? "",
    }).success;
  const isStep2Valid = () =>
    Boolean(
      formData.internType &&
        formData.subCategory &&
        formData.location &&
        formData.departmentID &&
        formData.startDate &&
        formData.endDate &&
        (!isHighSchool ||
          (formData.parentName.trim() &&
            formData.parentContact.trim() &&
            formData.coordinatorName.trim() &&
            formData.coordinatorContact.trim())),
    );
  const isStep3Valid = () =>
    Boolean(
      formData.school?.trim() &&
        formData.department?.trim() &&
        formData.class?.trim(),
    );
  const isFormValid = () =>
    isStep1Valid() && isStep2Valid() && isStep3Valid();

  // ─── Payload Builder ───────────────────────────────────────────
  const buildPayload = () => {
    const nameParts = formData.name.trim().split(/\s+/);
    const firstName = nameParts[0] ?? "";
    const lastName = nameParts.slice(1).join(" ") ?? "";
    const phoneDigits = phoneDigitsOnly(formData.phone);
    const phoneNumber =
      phoneDigits.length === 10 ? `+90${phoneDigits}` : formData.phone.trim();
    const identityNumber = (formData.tc ?? "").replace(/\s/g, "");

    return {
      phoneNumber,
      email: formData.email?.trim() || undefined,
      firstName,
      lastName,
      identityNumber,
      iban: formData.iban.trim() ? normalizeIban(formData.iban) : undefined,
      address: formData.address.trim() || undefined,
      school: formData.school.trim(),
      schoolDepartment: formData.department.trim(),
      grade: formData.class.trim(),
      schoolNumber: formData.schoolNumber.trim() || undefined,
      startDate: formData.startDate?.toISOString() ?? "",
      endDate: formData.endDate?.toISOString() ?? "",
      internshipType: INTERNSHIP_TYPE_V2_TO_API[formData.internType!],
      subCategory: SUB_CATEGORY_TO_API[formData.subCategory!],
      departmentID: formData.departmentID ?? undefined,
      status: formData.status,
      supervisorID: formData.supervisorID ?? undefined,
      supervisorName: formData.supervisorName?.trim() || undefined,
      supervisorContactInfo: formData.supervisorContact.trim() || undefined,
      // Lise özel alanları (BE: Lise için zorunlu)
      parentName: isHighSchool
        ? formData.parentName.trim() || undefined
        : undefined,
      parentContactInfo: isHighSchool
        ? formData.parentContact.trim() || undefined
        : undefined,
      coordinatorTeacherName: isHighSchool
        ? formData.coordinatorName.trim() || undefined
        : undefined,
      coordinatorTeacherContact: isHighSchool
        ? formData.coordinatorContact.trim() || undefined
        : undefined,
    };
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (currentStep !== STEPS.length - 1) return;
    if (!validateStep1()) {
      setCurrentStep(0);
      toast.error(
        t("internManagement.addModal.step1Error"),
      );
      return;
    }
    if (!validateStep2()) {
      setCurrentStep(1);
      toast.error(
        t("internManagement.addModal.step2Error"),
      );
      return;
    }
    if (!validateStep3()) {
      setCurrentStep(2);
      toast.error(
        t("internManagement.addModal.step3Error"),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      if (editId) {
        await internManagementApi.update(editId, payload);
        toast.success(
          t("internManagement.editModal.success"),
        );
      } else {
        await internManagementApi.create(
          payload as Parameters<typeof internManagementApi.create>[0],
        );
        toast.success(
          t("internManagement.addModal.success"),
        );
      }
      onSuccess?.(formData);
      onClose();
    } catch (error) {
      toast.error(
        translateInternApiError(
          error,
          t,
          t("internManagement.saveError"),
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const STEPS = [
    t("internManagement.addModal.step1"),
    t("internManagement.addModal.step2"),
    t("internManagement.addModal.step3"),
  ];

  const isEditMode = Boolean(editId);
  const customHeader = (
    <>
      <div className={styles.modalHeader}>
        <div className={styles.modalHeaderLeft}>
          <AddEmployeeIcon width={24} height={24} color="#14181F" />
          <h3 className={styles.modalTitle}>
            {isEditMode
              ? t("internManagement.editModal.title")
              : t("internManagement.addModal.title")}
          </h3>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t("buttons.close")}
        >
          <CloseIcon width={20} height={20} color="#72808f" />
        </button>
      </div>
      <div className={styles.stepTabs}>
        {STEPS.map((label, index) => (
          <div key={index} className={styles.stepTabWrapper}>
            <span
              className={`${styles.stepTab} ${currentStep === index ? styles.active : ""}`}
            >
              <span className={styles.stepNumber}>{index + 1}</span>
              {label}
            </span>
          </div>
        ))}
      </div>
      <div className={styles.stepProgressFull}>
        <div
          className={styles.stepProgressFullBar}
          style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
        />
      </div>
    </>
  );

  // ─── STEP 1 — Kişisel Bilgiler ──────────────────────────────────
  const step1Content = (
    <div className={styles.formGrid}>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.tc")}
          value={formData.tc}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 11);
            setFormData((prev) => ({ ...prev, tc: v }));
            if (fieldErrors.tc) setFieldErrors((p) => ({ ...p, tc: "" }));
          }}
          placeholder="Kimlik no girin..."
          type="number"
          maxLength={11}
          error={!!fieldErrors.tc}
          errorMessage={fieldErrors.tc}
        />
      </div>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.name")}
          value={formData.name}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, name: e.target.value }));
            if (fieldErrors.name) setFieldErrors((p) => ({ ...p, name: "" }));
          }}
          placeholder="Ad soyad girin..."
          error={!!fieldErrors.name}
          errorMessage={fieldErrors.name}
        />
      </div>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.phone")}
          value={formData.phone}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 10);
            setFormData((prev) => ({ ...prev, phone: v }));
            if (fieldErrors.phone)
              setFieldErrors((p) => ({ ...p, phone: "" }));
          }}
          placeholder={PHONE_PLACEHOLDER}
          type="tel"
          maxLength={10}
          error={!!fieldErrors.phone}
          errorMessage={fieldErrors.phone}
        />
      </div>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.email")}
          value={formData.email}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, email: e.target.value }));
            if (fieldErrors.email)
              setFieldErrors((p) => ({ ...p, email: "" }));
          }}
          placeholder="E-posta girin..."
          error={!!fieldErrors.email}
          errorMessage={fieldErrors.email}
        />
      </div>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.iban")}
          value={formData.iban}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, iban: e.target.value }));
            if (fieldErrors.iban)
              setFieldErrors((p) => ({ ...p, iban: "" }));
          }}
          placeholder="TR00 0000 0000 0000 0000 0000 00"
          error={!!fieldErrors.iban}
          errorMessage={fieldErrors.iban}
        />
      </div>
      <div className={`${styles.formField} ${styles.formFieldFull}`}>
        <Textarea
          label={t("internManagement.addModal.address")}
          value={formData.address}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, address: e.target.value }))
          }
          placeholder="Adres girin..."
          rows={2}
        />
      </div>
    </div>
  );

  // ─── STEP 2 — Staj Bilgileri (koşullu) ──────────────────────────
  const step2Content = (
    <div className={styles.formGrid}>
      <div className={styles.formField}>
        <Dropdown
          label={t("internManagement.addModal.internType")}
          placeholder={t("internManagement.addModal.internTypePlaceholder")}
          options={INTERN_TYPE_OPTIONS}
          value={formData.internType}
          selectedLabel={
            formData.internType
              ? INTERN_TYPE_OPTIONS.find(
                  (o) => o.value === formData.internType,
                )?.label
              : undefined
          }
          onSelect={(value) => {
            const v = Array.isArray(value) ? value[0] : value;
            setFormData((prev) => ({
              ...prev,
              internType: (v as InternTypeV2) ?? null,
            }));
            setFieldErrors((p) => ({ ...p, internType: "" }));
          }}
          errorMessage={fieldErrors.internType}
        />
      </div>
      <div className={styles.formField}>
        <Dropdown
          label={t("internManagement.addModal.subCategory")}
          placeholder={
            formData.internType
              ? t("internManagement.addModal.subCategoryPlaceholder")
              : t("internManagement.addModal.subCategoryPlaceholderEmpty")
          }
          options={subCategoryOptions}
          value={formData.subCategory}
          selectedLabel={
            formData.subCategory
              ? subCategoryOptions.find(
                  (o) => o.value === formData.subCategory,
                )?.label
              : undefined
          }
          onSelect={(value) => {
            const v = Array.isArray(value) ? value[0] : value;
            setFormData((prev) => ({
              ...prev,
              subCategory: (v as InternSubCategory) ?? null,
            }));
            setFieldErrors((p) => ({ ...p, subCategory: "" }));
          }}
          disabled={!formData.internType}
          errorMessage={fieldErrors.subCategory}
        />
      </div>
      <div className={styles.formField}>
        <Dropdown
          label={t("internManagement.addModal.location")}
          placeholder={t("internManagement.addModal.departmentTypePlaceholder")}
          options={LOCATION_OPTIONS}
          value={formData.location}
          selectedLabel={
            formData.location
              ? LOCATION_OPTIONS.find((o) => o.value === formData.location)
                  ?.label
              : undefined
          }
          onSelect={(value) => {
            const v = Array.isArray(value) ? value[0] : value;
            const next = (v as InternLocation) ?? null;
            setFormData((prev) =>
              prev.location === next
                ? prev
                : { ...prev, location: next, departmentID: null },
            );
            setFieldErrors((p) => ({ ...p, location: "", departmentID: "" }));
          }}
          errorMessage={fieldErrors.location}
        />
      </div>
      <div className={`${styles.formField} ${styles.formFieldFull}`}>
        <Dropdown
          label={t("internManagement.addModal.department")}
          placeholder={t("internManagement.addModal.departmentSelectPlaceholder")}
          options={departmentOptions}
          value={formData.departmentID}
          selectedLabel={
            formData.departmentID
              ? departmentOptions.find(
                  (o) => o.value === formData.departmentID,
                )?.label
              : undefined
          }
          onSelect={(value) => {
            const v = Array.isArray(value) ? value[0] : value;
            setFormData((prev) => ({ ...prev, departmentID: v ?? null }));
            setFieldErrors((p) => ({ ...p, departmentID: "" }));
          }}
          allowClear
          errorMessage={fieldErrors.departmentID}
        />
      </div>
      <div className={styles.formField}>
        <DatePicker
          label={t("internManagement.addModal.startDate")}
          placeholder={t("internManagement.addModal.datePickerPlaceholder")}
          value={formData.startDate}
          onChange={(value) =>
            setFormData((prev) => ({
              ...prev,
              startDate: Array.isArray(value) ? null : value,
            }))
          }
        />
      </div>
      <div className={styles.formField}>
        <DatePicker
          label={t("internManagement.addModal.endDate")}
          placeholder={t("internManagement.addModal.datePickerPlaceholder")}
          value={formData.endDate}
          onChange={(value) =>
            setFormData((prev) => ({
              ...prev,
              endDate: Array.isArray(value) ? null : value,
            }))
          }
        />
      </div>
      <div className={styles.formField}>
        <Dropdown
          label={t("internManagement.addModal.status")}
          placeholder={t("internManagement.addModal.statusPlaceholder2")}
          options={STATUS_OPTIONS}
          value={String(formData.status)}
          selectedLabel={
            formData.status === InternStatus.TAMAMLANDI ? t("internManagement.addModal.statusOptions.completed") : t("internManagement.addModal.statusOptions.active")
          }
          onSelect={(value) => {
            const v = Array.isArray(value) ? value[0] : value;
            const n = Number(v);
            setFormData((prev) => ({
              ...prev,
              status: (Number.isNaN(n) ? 0 : n) as InternStatus,
            }));
          }}
        />
      </div>
      <div className={styles.formField}>
        <Dropdown
          label={t("internManagement.addModal.supervisor")}
          placeholder={
            isLoadingSupervisor ? t("internManagement.addModal.supervisorLoading") : t("internManagement.addModal.supervisorSearchPlaceholder")
          }
          options={supervisorOptions}
          value={formData.supervisorID}
          selectedLabel={formData.supervisorName || undefined}
          onSelect={handleSupervisorSelect}
          onSearch={handleSupervisorSearch}
          searchable
          allowClear
        />
      </div>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.supervisorContact")}
          value={formData.supervisorContact}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              supervisorContact: e.target.value,
            }))
          }
          placeholder="Telefon veya e-posta..."
        />
      </div>
      <div className={`${styles.formField} ${styles.formFieldFull}`}>
        <Dropdown
          label={t("internManagement.addModal.applicationSource")}
          placeholder={t("internManagement.addModal.applicationSourcePlaceholderShort")}
          options={APPLICATION_SOURCE_V2_OPTIONS}
          value={formData.applicationSource}
          selectedLabel={
            formData.applicationSource
              ? APPLICATION_SOURCE_V2_OPTIONS.find(
                  (o) => o.value === formData.applicationSource,
                )?.label
              : undefined
          }
          onSelect={(value) => {
            if (lockApplicationSourceToInPerson) return;
            const v = Array.isArray(value) ? value[0] : value;
            setFormData((prev) => ({
              ...prev,
              applicationSource: (v as ApplicationSourceV2) ?? null,
            }));
          }}
          disabled={lockApplicationSourceToInPerson}
        />
        {lockApplicationSourceToInPerson && (
          <div className={styles.helperText}>
            {isHighSchool
              ? t("internManagement.addModal.highSchoolHint")
              : t("internManagement.addModal.workplaceHint")}
          </div>
        )}
      </div>

      {/* ── LİSE özel alanları (koşullu) ── */}
      {isHighSchool && (
        <>
          <div className={`${styles.formField} ${styles.formFieldFull}`}>
            <div className={styles.sectionDivider}>
              {t("internManagement.addModal.highSchoolSection")}
            </div>
          </div>
          <div className={styles.formField}>
            <Input
              label={t("internManagement.addModal.parentName")}
              value={formData.parentName}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, parentName: e.target.value }));
                if (fieldErrors.parentName)
                  setFieldErrors((p) => ({ ...p, parentName: "" }));
              }}
              placeholder={t("internManagement.addModal.parentNamePlaceholder")}
              error={!!fieldErrors.parentName}
              errorMessage={fieldErrors.parentName}
            />
          </div>
          <div className={styles.formField}>
            <Input
              label={t("internManagement.addModal.parentContact")}
              value={formData.parentContact}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  parentContact: e.target.value,
                }));
                if (fieldErrors.parentContact)
                  setFieldErrors((p) => ({ ...p, parentContact: "" }));
              }}
              placeholder="Telefon veya e-posta..."
              error={!!fieldErrors.parentContact}
              errorMessage={fieldErrors.parentContact}
            />
          </div>
          <div className={styles.formField}>
            <Input
              label={t("internManagement.addModal.coordinatorName")}
              value={formData.coordinatorName}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  coordinatorName: e.target.value,
                }));
                if (fieldErrors.coordinatorName)
                  setFieldErrors((p) => ({ ...p, coordinatorName: "" }));
              }}
              placeholder={t("internManagement.addModal.coordinatorPlaceholder")}
              error={!!fieldErrors.coordinatorName}
              errorMessage={fieldErrors.coordinatorName}
            />
          </div>
          <div className={styles.formField}>
            <Input
              label={t("internManagement.addModal.coordinatorContact")}
              value={formData.coordinatorContact}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  coordinatorContact: e.target.value,
                }));
                if (fieldErrors.coordinatorContact)
                  setFieldErrors((p) => ({ ...p, coordinatorContact: "" }));
              }}
              placeholder="Telefon veya e-posta..."
              error={!!fieldErrors.coordinatorContact}
              errorMessage={fieldErrors.coordinatorContact}
            />
          </div>
        </>
      )}
    </div>
  );

  // ─── STEP 3 — Akademik Bilgiler ─────────────────────────────────
  const step3Content = (
    <div className={styles.formGrid}>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.school")}
          value={formData.school}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, school: e.target.value }));
            if (fieldErrors.school)
              setFieldErrors((p) => ({ ...p, school: "" }));
          }}
          placeholder={t("internManagement.addModal.schoolPlaceholderShort")}
          error={!!fieldErrors.school}
          errorMessage={fieldErrors.school}
        />
      </div>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.schoolDepartment")}
          value={formData.department}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, department: e.target.value }));
            if (fieldErrors.department)
              setFieldErrors((p) => ({ ...p, department: "" }));
          }}
          placeholder={t("internManagement.addModal.schoolDepartmentPlaceholder")}
          error={!!fieldErrors.department}
          errorMessage={fieldErrors.department}
        />
      </div>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.class")}
          value={formData.class}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, class: e.target.value }));
            if (fieldErrors.class)
              setFieldErrors((p) => ({ ...p, class: "" }));
          }}
          placeholder={t("internManagement.addModal.classPlaceholderShort")}
          error={!!fieldErrors.class}
          errorMessage={fieldErrors.class}
        />
      </div>
      <div className={styles.formField}>
        <Input
          label={t("internManagement.addModal.schoolNumber")}
          value={formData.schoolNumber}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, schoolNumber: e.target.value }))
          }
          placeholder={t("internManagement.addModal.schoolNumberPlaceholder")}
        />
      </div>
    </div>
  );

  const handleContinue = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentStep === 0 && !validateStep1()) {
      toast.error(
        t("internManagement.addModal.step1Error"),
      );
      return;
    }
    if (currentStep === 1 && !validateStep2()) {
      toast.error(
        t("internManagement.addModal.step2Error"),
      );
      return;
    }
    setCurrentStep((s) => s + 1);
  };

  const handleBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  const customFooter = (
    <div className={styles.footerButtons} onClick={(e) => e.stopPropagation()}>
      <Button variant="text" color="secondary" type="button" onClick={onClose}>
        {t("buttons.cancel")}
      </Button>
      <div className={styles.footerActions}>
        {currentStep > 0 && (
          <Button
            variant="outlined"
            color="secondary"
            type="button"
            onClick={handleBack}
          >
            {t("internManagement.addModal.back")}
          </Button>
        )}
        {currentStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={handleContinue}
          >
            {t("internManagement.addModal.continue")}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="addInternForm"
            disabled={isSubmitting || editLoading || !isFormValid()}
            title={
              !isFormValid()
                ? t("internManagement.addModal.requiredFieldsError")
                : undefined
            }
          >
            {isSubmitting
              ? t("common.loading")
              : isEditMode
                ? t("internManagement.editModal.submit")
                : t("internManagement.addModal.submit")}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("internManagement.addModal.title")}
      customHeader={customHeader}
      customFooter={customFooter}
      isMultiStep
      hideStepProgress
      currentStep={currentStep}
    >
      <form
        id="addInternForm"
        onSubmit={handleSubmit}
        className={styles.form}
        onClick={(e) => e.stopPropagation()}
      >
        {currentStep === 0 && step1Content}
        {currentStep === 1 && step2Content}
        {currentStep === 2 && step3Content}
      </form>
    </Modal>
  );
};

/** Eski combined başvuru kaynağını yeni V2 değerine map et (edit mode için) */
function mapLegacyApplicationSourceToV2(
  legacy: ApplicationSource | undefined,
): ApplicationSourceV2 | null {
  if (legacy === "kariyerKapisi") return "CAREER_GATE";
  if (legacy === "manuel") return "IN_PERSON";
  return null;
}

export default AddInternModal;
